<script setup lang="ts">
import { TOTAL_MONTH_HOURS, displayName } from '@schwrz/shared';
import {
  statusForPct,
  statusLabelRu,
  statusModifier,
} from '@schwrz/shared/metric-status';
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { api } from '../../shared/api/client';
import { tierEmojiForIndex } from '../../shared/lib/tierBadges';
import { round1 } from '../../shared/lib/userHealth';
import UserAvatar from '../../shared/ui/UserAvatar.vue';
import { useAuthStore } from '../../stores/auth';
import { useBonusShopStore } from '../../stores/bonusShop';
import { useClientsStore } from '../../stores/clients';
import { useHomeViewStore } from '../../stores/homeView';
import { useProjectsStore } from '../../stores/projects';
import { useWorkspaceUsersStore } from '../../stores/workspaceUsers';

interface UserHoursRow {
  userId: string;
  firstName: string;
  lastName: string;
  openHours: number;
  allocatedHours: number;
  doneHours: number;
  openCount: number;
  doneCount: number;
  overdueCount: number;
  tierIndex: number;
}

const homeView = useHomeViewStore();
const auth = useAuthStore();
const bonusShop = useBonusShopStore();
const clientsStore = useClientsStore();
const projectsStore = useProjectsStore();
const workspaceUsers = useWorkspaceUsersStore();
const router = useRouter();
const route = useRoute();

const rows = ref<UserHoursRow[]>([]);
const loading = ref(false);
const hoveredUserId = ref<string | null>(null);
const popoverPos = ref({ top: 0, left: 0 });

function showPopover(event: MouseEvent | FocusEvent, userId: string): void {
  const el = event.currentTarget;
  if (!(el instanceof HTMLElement)) return;
  const rect = el.getBoundingClientRect();
  hoveredUserId.value = userId;
  popoverPos.value = {
    top: rect.bottom + 8,
    left: rect.left + rect.width / 2,
  };
}

const showStats = computed(() => !!auth.user?.isAdmin);

function avatarAriaLabel(u: { name: string; allocatedHours?: number; allocPct?: number }): string {
  if (showStats.value && u.allocatedHours !== undefined && u.allocPct !== undefined) {
    return `${u.name}: ${u.allocatedHours} ч (${u.allocPct}%)`;
  }
  return u.name;
}

function onAvatarHover(event: MouseEvent | FocusEvent, userId: string): void {
  if (!showStats.value) return;
  showPopover(event, userId);
}

function onAvatarUnhover(): void {
  if (!showStats.value) return;
  hidePopover();
}

function hidePopover(): void {
  hoveredUserId.value = null;
}

function popoverFor(userId: string) {
  return enriched.value.find((u) => u.userId === userId) ?? null;
}

const activePopover = computed(() =>
  hoveredUserId.value ? popoverFor(hoveredUserId.value) : null,
);

function selectUser(userId: string): void {
  if (homeView.selectedUserId === userId) {
    homeView.selectUser(null);
    return;
  }
  homeView.selectUser(userId);
  clientsStore.select(null);
  projectsStore.select(null);
  if (route.name !== 'home') {
    void router.push({ name: 'home' });
  }
}

onMounted(async () => {
  loading.value = true;
  try {
    await workspaceUsers.load();
    if (!showStats.value) return;

    await bonusShop.loadProgram();
    const loads: Promise<unknown>[] = [
      api<{ rows: UserHoursRow[] }>('/api/reports/user-hours').then((r) => {
        rows.value = r.rows;
      }),
    ];
    if (bonusShop.gamificationEnabled) {
      loads.push(bonusShop.loadTiers());
    }
    await Promise.all(loads);
  } finally {
    loading.value = false;
  }
});

const enriched = computed(() =>
  rows.value.map((r) => {
    const wsUser = workspaceUsers.byId(r.userId);
    const allocated = r.allocatedHours ?? r.openHours;
    const allocPct = round1((allocated / TOTAL_MONTH_HOURS) * 100);
    const status = statusForPct(allocPct, 'band');
    return {
      userId: r.userId,
      firstName: wsUser?.firstName ?? r.firstName,
      lastName: wsUser?.lastName ?? r.lastName,
      avatarUrl: wsUser?.avatarUrl ?? null,
      name: wsUser ? displayName(wsUser) : displayName(r),
      allocatedHours: round1(allocated),
      doneHours: round1(r.doneHours),
      openCount: r.openCount,
      doneCount: r.doneCount,
      overdueCount: r.overdueCount,
      allocPct,
      barPct: Math.min(100, allocPct),
      status,
      statusModifier: statusModifier(status),
      statusLabel: statusLabelRu(status, 'band'),
      tierIndex: r.tierIndex,
      emoji: tierEmojiForIndex(r.tierIndex, bonusShop.tiers),
    };
  }),
);

const stripUsers = computed(() => {
  if (showStats.value && enriched.value.length > 0) {
    return enriched.value;
  }
  return workspaceUsers.employees.map((u) => ({
    userId: u.id,
    firstName: u.firstName,
    lastName: u.lastName,
    avatarUrl: u.avatarUrl ?? null,
    name: displayName(u),
  }));
});
</script>

<template>
  <nav
    class="header-users-strip"
    aria-label="Сотрудники"
  >
    <p
      v-if="loading && stripUsers.length === 0"
      class="header-users-strip__hint hint"
    >
      Загрузка...
    </p>
    <ul
      v-else-if="stripUsers.length > 0"
      class="header-users-strip__list"
    >
      <li
        v-for="u in stripUsers"
        :key="u.userId"
        class="header-users-strip__item"
      >
        <button
          type="button"
          class="header-users-strip__btn"
          :class="{ 'is-active': homeView.selectedUserId === u.userId }"
          :aria-label="avatarAriaLabel(u)"
          :aria-pressed="homeView.selectedUserId === u.userId"
          @mouseenter="onAvatarHover($event, u.userId)"
          @mouseleave="onAvatarUnhover"
          @focus="onAvatarHover($event, u.userId)"
          @blur="onAvatarUnhover"
          @click="selectUser(u.userId)"
        >
          <UserAvatar
            :first-name="u.firstName"
            :last-name="u.lastName"
            :avatar-url="u.avatarUrl"
            size="sm"
            decorative
          />
        </button>
      </li>
    </ul>
    <Teleport to="body">
      <div
        v-if="showStats && activePopover"
        class="header-users-strip__popover header-users-strip__popover--floating"
        :style="{
          top: `${popoverPos.top}px`,
          left: `${popoverPos.left}px`,
        }"
        role="tooltip"
      >
        <p class="header-users-strip__name">
          {{ activePopover.name }}
          <span
            v-if="bonusShop.gamificationEnabled"
            class="header-users-strip__tier"
            aria-hidden="true"
          >{{ activePopover.emoji }}</span>
        </p>
        <div
          class="header-users-strip__bar-track"
          role="progressbar"
          :aria-valuenow="Math.round(activePopover.allocPct)"
          aria-valuemin="0"
          aria-valuemax="100"
          :aria-label="`${activePopover.allocPct}% от нормы — ${activePopover.statusLabel}`"
        >
          <span
            class="header-users-strip__bar-fill"
            :class="`header-users-strip__bar-fill--${activePopover.statusModifier}`"
            :style="{ width: activePopover.barPct + '%' }"
          ></span>
        </div>
        <div class="header-users-strip__metrics">
          <span
            :class="`header-users-strip__pct header-users-strip__pct--${activePopover.statusModifier}`"
          >
            {{ activePopover.allocPct }}%
          </span>
          <span class="header-users-strip__hours">{{ activePopover.allocatedHours }} ч</span>
          <span class="header-users-strip__status">{{ activePopover.statusLabel }}</span>
        </div>
        <p class="header-users-strip__tasks">
          <span>{{ activePopover.doneCount }} гот.</span>
          <span>{{ activePopover.openCount }} откр.</span>
          <span
            v-if="activePopover.overdueCount > 0"
            class="header-users-strip__overdue"
          >
            {{ activePopover.overdueCount }} проср.
          </span>
        </p>
      </div>
    </Teleport>
  </nav>
</template>

<style scoped>
.header-users-strip {
  width: 100%;
  max-width: min(920px, calc(100vw - 10rem));
}

.header-users-strip__hint {
  margin: 0;
  text-align: center;
  font-size: 0.72rem;
}

.header-users-strip__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: center;
  gap: 0.28rem;
  overflow-x: auto;
  scrollbar-width: thin;
}

.header-users-strip__item {
  position: relative;
  flex: 0 0 auto;
}

.header-users-strip__btn {
  display: flex;
  border: none;
  background: transparent;
  padding: 0.12rem;
  border-radius: 999px;
  cursor: pointer;
  color: inherit;
  transition:
    box-shadow var(--transition-fast),
    transform var(--transition-fast);
}

.header-users-strip__btn:hover {
  transform: translateY(-1px);
}

.header-users-strip__btn:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}

.header-users-strip__btn.is-active {
  box-shadow: 0 0 0 2px var(--accent);
}

.header-users-strip__popover {
  min-width: 11.5rem;
  max-width: 14rem;
  padding: 0.55rem 0.65rem;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--surface);
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.28);
  pointer-events: none;
  z-index: 220;
}

.header-users-strip__popover--floating {
  position: fixed;
  transform: translateX(-50%);
}

.header-users-strip__name {
  margin: 0 0 0.35rem;
  font-size: 0.78rem;
  font-weight: 650;
  line-height: 1.25;
  display: flex;
  align-items: center;
  gap: 0.3rem;
}

.header-users-strip__tier {
  font-size: 0.95rem;
  line-height: 1;
}

.header-users-strip__bar-track {
  position: relative;
  height: 5px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--muted) 22%, var(--bg) 78%);
  overflow: hidden;
}

.header-users-strip__bar-fill {
  display: block;
  height: 100%;
  border-radius: inherit;
}

.header-users-strip__bar-fill--idle { background: var(--chart-status-idle); }
.header-users-strip__bar-fill--good { background: var(--chart-status-good); }
.header-users-strip__bar-fill--warn { background: var(--chart-status-warn); }
.header-users-strip__bar-fill--bad { background: var(--chart-status-bad); }
.header-users-strip__bar-fill--na { background: color-mix(in srgb, var(--muted) 55%, var(--bg) 45%); }

.header-users-strip__metrics {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-top: 0.32rem;
  font-size: 0.72rem;
  font-variant-numeric: tabular-nums;
}

.header-users-strip__pct {
  font-weight: 700;
}

.header-users-strip__pct--idle { color: var(--chart-status-idle); }
.header-users-strip__pct--good { color: var(--chart-status-good); }
.header-users-strip__pct--warn { color: var(--chart-status-warn); }
.header-users-strip__pct--bad { color: var(--chart-status-bad); }

.header-users-strip__hours {
  color: var(--muted);
}

.header-users-strip__status {
  margin-left: auto;
  color: var(--muted);
}

.header-users-strip__tasks {
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  margin: 0.28rem 0 0;
  font-size: 0.68rem;
  color: var(--muted);
}

.header-users-strip__overdue {
  color: var(--danger, #c0492f);
  font-weight: 600;
}

@media (max-width: 720px) {
  .header-users-strip {
    max-width: none;
  }

  .header-users-strip__list {
    justify-content: center;
  }
}

@media (prefers-reduced-motion: reduce) {
  .header-users-strip__btn,
  .header-users-strip__popover {
    transition: none;
  }
}
</style>
